import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { N0, N200 } from '@atlaskit/theme/colors';
import {
  ProductKey,
  SanitizedFormattedHtmlMessage
} from '@atlassiansox/bxpkit-core';
import styled from '@emotion/styled';
import React from 'react';
import { injectIntl } from 'react-intl';
import { useUserInfo } from '../../hook';
import { getBitbucketWorkspaces } from '../../hook/use-bitbucket-workspaces';
import {
  markDevopsLoadingStart,
  submitDevopsTimeToInteractive
} from '../../metrics';
import { DevopsHeader, SideInfoDevops } from './component';
import { DevopsFormView } from './devops-form-view';
import { DevopsContextProps, DevopsProvider } from './hook/use-devops-state';
import { messages } from './messages';
import { DevopsImageUrlsType } from './types';

function DevopsScreen(props: DevopsContextProps) {
  const { devopsImageUrls } = props;
  const userInfo = useUserInfo();
  if (userInfo) {
    // filter out sites with no adminAccess, and sites that include all 4 Devops products
    // opsgenie is shadow provisioned with Jira Service Managment, so check for both or errors will occur
    const sites = (userInfo?.sites || [])
      .filter((site) => site?.adminAccess)
      .filter((site) => {
        return !(
          site.products.includes(ProductKey.JIRA_SOFTWARE) &&
          site.products.includes(ProductKey.CONFLUENCE) &&
          site.products.includes(ProductKey.BITBUCKET) &&
          (site.products.includes(ProductKey.JIRA_SERVICE_MANAGEMENT) ||
            site.products.includes(ProductKey.OPSGENIE))
        );
      });
    Object.assign(userInfo, { sites });
  }

  const bbworkspaces = getBitbucketWorkspaces(!!userInfo?.hasBitbucket) || [];
  const { createAnalyticsEvent } = useAnalyticsEvents();
  const recaptchaEulaAnalytics = () => {
    createAnalyticsEvent({
      eventName: 'bxp.formCloudSignup.reCaptchaPrivacyPolicyClicked',
      eventComponent: 'button',
      event: 'clicked'
    }).fire();
  };

  markDevopsLoadingStart();
  submitDevopsTimeToInteractive();

  return (
    <DevopsProvider {...props} userInfo={userInfo} bbworkspaces={bbworkspaces}>
      <PageRoot devopsImageUrls={devopsImageUrls}>
        <DevopsHeader />
        <PageBody>
          <FormWrapper>
            <Column className="wrapper-side-info">
              <SideInfoDevops />
            </Column>
            <Column>
              <SignupWrapper>
                <DevopsFormView />
              </SignupWrapper>
              <RecaptchaLegal onClick={recaptchaEulaAnalytics}>
                <SanitizedFormattedHtmlMessage {...messages.devopsRecaptcha} />
              </RecaptchaLegal>
            </Column>
          </FormWrapper>
        </PageBody>
      </PageRoot>
    </DevopsProvider>
  );
}

export const PageRoot = styled.div<{
  readonly devopsImageUrls?: DevopsImageUrlsType;
}>`
  position: relative;
  width: 100%;
  margin: 0;
  padding: 0;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  background-image: url(${({ devopsImageUrls }) =>
    devopsImageUrls?.backgroundImage});
  @media only screen and (max-width: 800px) {
    background-image: none;
  }
`;

export const PageBody = styled.div`
  position: relative;
  max-width: 1200px;
  margin: auto;

  .logo-atl {
    display: block;
    margin: 26px auto 26px;
  }
`;

const FormWrapper = styled.div`
  z-index: 9;
  margin: auto;
  display: flex;
  flex-direction: row;
  flex-flow: nowrap;
  padding: 0px 48px 48px 48px;

  @media only screen and (max-width: 800px) {
    box-sizing: border-box;
    flex-flow: row wrap;
    padding: 24px;
    margin-top: 12px;
  }
`;

const Column = styled.div`
  width: 50%;

  @media only screen and (max-width: 800px) {
    width: 100%;
  }

  &.wrapper-side-info {
    padding: 0 48px 0 0;

    @media only screen and (max-width: 800px) {
      display: none;
    }
  }
`;

const SignupWrapper = styled.div`
  padding: 48px 60px;
  margin-top: 30px;
  background-color: ${N0};
  box-shadow: rgba(98, 183, 203, 0.26) 0px 8px 16px,
    rgba(98, 183, 203, 0.31) 0px 0px 1px;
  border-radius: 3px;

  form {
    max-width: none;
  }

  @media only screen and (max-width: 1200px) {
    padding: 48px;
  }

  @media only screen and (max-width: 800px) {
    padding: 0px;
    margin-top: 0px;
    box-shadow: none;
  }
`;

const RecaptchaLegal = styled.div`
  display: block;
  max-width: 296px;
  padding: 25px 0;
  margin: auto;
  text-align: center;
  font-family: Charlie Text, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto,
    Noto Sans, Ubuntu, Droid Sans, Helvetica Neue, sans-serif;
  font-size: 12px;
  line-height: 18px;
  color: ${N200};

  @media only screen and (max-width: 800px) {
    padding: 50px 0 60px 0;
  }
`;

DevopsScreen.messages = messages;
export default injectIntl(DevopsScreen);
